import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  ModalFooter,
  Card,
  CardTitle,
  CardBody,
  Collapse,
  Col,
  Row,
  Input,
  Label,
} from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { GetLicenseManagementCourses } from "../../actions/superUserQueries";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CreateNewScormLicenseCourse,
  UpdateLicenseCourse,
} from "../../actions/superUserMutations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";

import { formatDate } from "../../Utilities/dateUtils";
import DataGrid from "../DataGrids/DataGrid";

function LicMgtHandleCoursesModal({ toggle, isOpen }) {
  const intl = useIntl();
  const [rows, setRows] = useState([]);
  const [newCourse, setNewCourse] = useState({ name: "" });
  const [addNewCourseOpen, setAddNewCourseOpen] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editValues, setEditValues] = useState({});

  const licenseCourses = GetLicenseManagementCourses();

  const handleAddNewScormLicenseCourse = useMutation({
    mutationFn: CreateNewScormLicenseCourse,
  });

  const handleUpdateLicenseCourse = useMutation({ mutationFn: UpdateLicenseCourse });

  const queryClient = useQueryClient();

  const handleToggle = () => {
    setAddNewCourseOpen(false);
    setEditingRowId(null);
    setEditValues({});
    toggle();
  };

  useEffect(() => {
    const courses = [];
    if (!licenseCourses.isPending && licenseCourses.data) {
      licenseCourses.data.forEach((c) => {
        let course = {
          id: c.id,
          name: c.name,
          identifier: c.identifier,
          additionalInfo: c.additionalInfo,
          created: formatDate(c.created, "P"),
        };
        courses.push(course);
      });
      setRows(courses);
    }
  }, [licenseCourses.data, licenseCourses.isPending]);

  const handleStartEdit = (rowId) => {
    const row = rows.find((r) => r.id === rowId);
    setEditingRowId(rowId);
    setEditValues({
      name: row.name,
      identifier: row.identifier,
      additionalInfo: row.additionalInfo,
    });
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setEditValues({});
  };

  const handleSaveEdit = () => {
    const row = rows.find((r) => r.id === editingRowId);
    const updatedRow = { ...row, ...editValues };
    const changedRows = rows.map((r) =>
      r.id === editingRowId ? updatedRow : r,
    );

    handleUpdateLicenseCourse.mutate(
      {
        id: updatedRow.id,
        name: updatedRow.name,
        identifier: updatedRow.identifier,
        additionalInfo: updatedRow.additionalInfo,
      },
      {
        onSuccess: (data) => {
          if (data && data.status > 199 && data.status < 300) {
            toast.success("Kurs uppdaterad");
            setRows(changedRows);
            setEditingRowId(null);
            setEditValues({});
          } else {
            toast.warn(data.response.data);
          }
        },
        throwOnError: true,
        onError: (err) => {
          toast.warn(err);
        },
      },
    );
  };

  const editableCell = (field) => (props) => {
    const rowId = props.row.original.id;
    if (rowId === editingRowId) {
      return (
        <Input
          type="text"
          value={editValues[field] ?? ""}
          onChange={(e) =>
            setEditValues({ ...editValues, [field]: e.target.value })
          }
        />
      );
    }
    return props.getValue();
  };

  const columns = [
    {
      id: "name",
      key: "name",
      header: <FormattedMessage id="Name" />,
      width: 150,
      cell: editableCell("name"),
    },
    {
      id: "identifier",
      key: "identifier",
      header: "Identifierare",
      width: 170,
      cell: editableCell("identifier"),
    },
    {
      id: "additionalInfo",
      key: "additionalInfo",
      header: "Övrig info",
      width: 250,
      cell: editableCell("additionalInfo"),
    },
    {
      id: "created",
      key: "created",
      header: <FormattedMessage id="Created" />,
      width: 170,
    },
    {
      id: "actions",
      key: "id",
      header: "",
      width: 120,
      sortable: false,
      cell: (props) => {
        const rowId = props.row.original.id;
        if (rowId === editingRowId) {
          return (
            <div className="d-flex gap-1">
              <Button
                size="sm"
                outline
                color="primary"
                onClick={handleSaveEdit}
              >
                {intl.formatMessage({ id: "Save" })}
              </Button>
              <Button
                size="sm"
                outline
                color="secondary"
                onClick={handleCancelEdit}
              >
                {intl.formatMessage({ id: "Cancel" })}
              </Button>
            </div>
          );
        }
        return (
          <Button
            size="sm"
            outline
            color="primary"
            onClick={() => handleStartEdit(rowId)}
          >
            {intl.formatMessage({ id: "Edit" })}
          </Button>
        );
      },
    },
  ];

  const handleAddNewCourse = () => {
    if (newCourse.name.length > 0) {
      handleAddNewScormLicenseCourse.mutate(
        {
          name: newCourse.name,
          identifier: newCourse.identifier,
          additionalInfo: newCourse.additionalInfo,
        },
        {
          onSuccess: (data) => {
            if (data && data.status > 199 && data.status < 300) {
              toast.success(intl.formatMessage({ id: "CourseCreated" }));
              setAddNewCourseOpen(false);
              queryClient.setQueryData(
                ["FetchLicenseManagementCourses"],
                [...licenseCourses.data, data.data.result]
              );
            } else {
              toast.warn(data.response.data);
            }
          },
          throwOnError: true,
          onError: (err) => {
            toast.warn(err);
          },
        }
      );
    }
  };

  return (
    <Modal
      size="xl"
      style={{ maxWidth: "70%", width: "100%" }}
      isOpen={isOpen}
      toggle={handleToggle}
    >
      <ModalHeader>
        <FormattedMessage id="Courses" />
      </ModalHeader>
      <ModalBody>
        <Card>
          <CardBody>
            <CardTitle>
              <FormattedMessage id="Courses" />{" "}
              <Button
                className="ms-3"
                outline
                color="primary"
                onClick={() => setAddNewCourseOpen(!addNewCourseOpen)}
              >
                <FontAwesomeIcon
                  style={{ marginRight: ".5em" }}
                  className={
                    addNewCourseOpen
                      ? "fa-caret--animated collapsed"
                      : "fa-caret--animated"
                  }
                  icon={"caret-up"}
                />
                <FormattedMessage id="AddCourse" />
              </Button>
            </CardTitle>

            <Collapse isOpen={addNewCourseOpen} className="border my-3 p-2">
              <>
                <Row className="mt-3 mb-1">
                  <Col>
                    <Label className="me-2 mb-0" for="newCourseName">
                      <FormattedMessage id="CourseName" />:
                    </Label>
                    <Input
                      name="newCourseName"
                      type="text"
                      onChange={(data) =>
                        setNewCourse({ ...newCourse, name: data.target.value })
                      }
                    />
                  </Col>
                  <Col>
                    <Label className="me-2 mb-0" for="newCourseIdentifier">
                      <FormattedMessage id="IdentifierForCourse" />:
                    </Label>
                    <Input
                      name="newCourseIdentifier"
                      type="text"
                      onChange={(data) =>
                        setNewCourse({
                          ...newCourse,
                          identifier: data.target.value,
                        })
                      }
                    />
                  </Col>
                  <Col>
                    <Label className="me-2 mb-0" for="newCourseAdditionalInfo">
                      <FormattedMessage id="AdditionalInfo" />:
                    </Label>
                    <Input
                      name="newCourseAdditionalInfo"
                      type="text"
                      onChange={(data) =>
                        setNewCourse({
                          ...newCourse,
                          additionalInfo: data.target.value,
                        })
                      }
                    />
                  </Col>
                </Row>
                <Row className="pt-1">
                  <Col>
                    <Button
                      outline
                      className="float-end"
                      color="primary"
                      onClick={() => handleAddNewCourse()}
                    >
                      <FormattedMessage id="AddCourse" />
                    </Button>
                    <Button
                      color="secondary"
                      outline
                      className="me-2 float-end"
                      onClick={() => setAddNewCourseOpen(false)}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Col>
                </Row>
              </>
            </Collapse>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
            />
          </CardBody>
        </Card>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={handleToggle}>
          <FormattedMessage id="Cancel" />
        </Button>
      </ModalFooter>
    </Modal>
  );
}
export default LicMgtHandleCoursesModal;
